import type { ZodType } from 'zod';
import type { VersionData as _LatestVersionSchema } from './schemas/v3';
type Version = number;
export interface CommonVersionData {
  version: Version;
  [key: string]: unknown;
}
export type LatestVersionSchema = _LatestVersionSchema;
export const LATEST_VERSION = 3 as const;

/**
 * 获取数据的版本号
 */
export function getDataVersion(data: unknown): Version | null {
  if (typeof data !== 'object' || data === null) {
    return null;
  }

  const versionedData = data as CommonVersionData;
  const version = versionedData.version;

  if (typeof version !== 'number') {
    return null;
  }

  return version;
}

interface ISchemaModule {
  versionSchema: ZodType;
}

interface IMigrationModule {
  migrate: (data: unknown) => unknown;
}

/**
 * 验证数据是否符合指定版本的 Schema
 */
export async function validateVersion(data: unknown, version: Version): Promise<boolean> {
  // 模板字面量的动态 import 拿不到模块类型，只能在这里声明形状
  const schemaModule: ISchemaModule = await import(`./schemas/v${version}.ts`);

  if (!schemaModule) {
    throw new Error(`没找到版本${version}的Schema`);
  }

  const result = schemaModule['versionSchema'].safeParse(data);
  if (!result.success) {
    console.error('result', result.error);
  }
  return result.success;
}

/**
 * 将数据升级到最新版本
 * 支持管道式升级，自动查找迁移路径
 */
export async function upgradeToLatest(data: unknown): Promise<_LatestVersionSchema> {
  const currentVersion = getDataVersion(data);

  if (currentVersion === null) {
    throw new Error('无法识别数据版本，数据必须包含 version 字段');
  } else if (currentVersion > LATEST_VERSION) {
    throw new Error(`数据版本错误: 当前版本 ${currentVersion} 大于最新版本 ${LATEST_VERSION}`);
  }

  const isValid = await validateVersion(data, currentVersion);
  if (!isValid) {
    throw new Error(`数据格式不符合版本 ${currentVersion} 的规范`);
  }

  if (currentVersion === LATEST_VERSION) {
    // 已经是最新版本，直接返回
    return data as _LatestVersionSchema;
  }

  // 查找迁移路径
  const migrationPath: IMigrationModule['migrate'][] = [];
  for (let i = currentVersion; i < LATEST_VERSION; i++) {
    const migrationFileName = `v${i}-to-v${i + 1}`;
    const migrationFn: IMigrationModule = await import(`./migrations/${migrationFileName}.ts`);
    if (!migrationFn) throw new Error(`没找到版本${i}到版本${i + 1}的迁移函数`);
    migrationPath.push(migrationFn['migrate']);
  }

  for (const migrateFn of migrationPath) {
    data = migrateFn(data);
  }

  return data as _LatestVersionSchema;
}
